import { SerializedError, createAsyncThunk } from '@reduxjs/toolkit';
import { makeRequest, ApiRequestError } from './apiRequest';
import { HttpErrorStatus } from './httpStatusCodes';
import { setErrorToast } from './errorToast.util';
import { ApiRequestParams, ApiRequestDefaults } from './apiRequestParams';
import { EndpointDefinition } from './endpointDefinition';
import stringify from 'json-stable-stringify';

// Simple request deduplication to prevent duplicate API calls
const pendingRequests = new Map<string, Promise<any>>();

export const createApiThunk = <
  RESPONSE extends object,
  BODY extends object,
  PARAMS extends ApiRequestParams
>(
  endpoint: EndpointDefinition<RESPONSE, BODY, PARAMS>,
  caughtErrors?: HttpErrorStatus[]
) => {
  const thunk = createAsyncThunk<
    RESPONSE,
    {
      params?: PARAMS;
      body?: BODY;
      defaults?: ApiRequestDefaults;
    },
    {
      rejectValue: CustomSerializedError;
    }
  >(
    endpoint.name,
    async (
      request: { params?: PARAMS; body?: BODY; defaults?: ApiRequestDefaults },
      thunkAPI
    ) => {
      // Create a cache key for this request using deterministic stringification
      const cacheKey = `${endpoint.name}-${stringify(request)}`;

      // If there's already a pending request for this key, return it
      if (pendingRequests.has(cacheKey)) {
        return pendingRequests.get(cacheKey);
      }

      // Create the request promise
      const requestPromise = (async () => {
        try {
          const result = await makeRequest(endpoint, request);
          return result;
        } catch (err) {
          const apiError = err as ApiRequestError;
          setErrorToast(apiError, thunkAPI.dispatch);

          const serializedError = err as SerializedError;
          if (
            caughtErrors &&
            caughtErrors.find(
              httpError => httpError.toString() === serializedError.code
            )
          ) {
            return thunkAPI.rejectWithValue({
              ...serializedError,
              isAccessDenied: apiError.isAccessDenied,
              errorIsCaught: true,
            });
          }
          return thunkAPI.rejectWithValue({
            ...serializedError,
            isAccessDenied: apiError.isAccessDenied,
            errorIsCaught: false,
          });
        } finally {
          // Remove from pending requests when done
          pendingRequests.delete(cacheKey);
        }
      })();

      // Store the promise in the cache
      pendingRequests.set(cacheKey, requestPromise);

      return requestPromise;
    }
  );

  return thunk;
};

export type CustomSerializedError = SerializedError & {
  errorIsCaught: boolean;
  isAccessDenied?: boolean;
};
